"use client";

/**
 * ContactDetailDisplay - Single contact detail display
 */

import { useState } from "react";
import { Tag, Avatar } from "@/ui";
import { colors, spacing, textStyles } from "@/ui/tokens";
import { displayValue } from "../utils";
import {
  Business,
  Email,
  Phone,
  LocationOn,
  LocalOffer,
  Notes,
  PersonOff,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
  ToolDivider,
  ToolSectionHeader,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";

interface ContactDetail {
  id: string | number;
  name?: string;
  company?: string | { id?: number; name?: string; [key: string]: unknown };
  email?: string;
  phone?: string;
  title?: string;
  location?: string;
  avatar?: string;
  notes?: string;
  tags?: Array<string | { id?: number; name?: string; [key: string]: unknown }>;
  type?: string;
}

interface DetailOutput {
  success?: boolean;
  contact?: ContactDetail;
  error?: string;
}

export function ContactDetailDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as DetailOutput | undefined;
  const contact = result?.contact;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={contact?.name || "Contact Details"}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Business style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Loading contact..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && contact && (
          <>
            <div style={{ padding: spacing[5], display: "flex", gap: spacing[5], alignItems: "flex-start" }}>
              <Avatar
                src={contact.avatar}
                name={contact.name || `Contact #${contact.id}`}
                size="xl"
              />
              <div style={{ flex: 1 }}>
                <span style={{ ...textStyles.subtitle1, fontWeight: 600, display: "block" }}>
                  {contact.name}
                </span>
                {contact.company && (
                  <span style={{ ...textStyles.body1, color: colors.text.muted, display: "block" }}>
                    {displayValue(contact.company)}
                  </span>
                )}
                {contact.title && (
                  <span style={{ ...textStyles.body2, color: colors.text.muted, display: "block" }}>
                    {contact.title}
                  </span>
                )}
                <div style={{ display: "flex", gap: spacing[4], marginTop: spacing[2], flexWrap: "wrap" }}>
                  {contact.email && (
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                      <Email style={{ fontSize: 16, color: colors.text.muted }} />
                      <span style={textStyles.body2}>{contact.email}</span>
                    </div>
                  )}
                  {contact.phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                      <Phone style={{ fontSize: 16, color: colors.text.muted }} />
                      <span style={textStyles.body2}>{contact.phone}</span>
                    </div>
                  )}
                  {contact.location && (
                    <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                      <LocationOn style={{ fontSize: 16, color: colors.text.muted }} />
                      <span style={textStyles.body2}>{contact.location}</span>
                    </div>
                  )}
                </div>
                {contact.type && (
                  <div style={{ marginTop: spacing[2] }}>
                    <Tag size="sm" variant="filled" color="primary">
                      {contact.type}
                    </Tag>
                  </div>
                )}
              </div>
            </div>

            {contact.notes && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<Notes />} label="Notes" compact iconColor={colors.text.muted} />
                  <span style={textStyles.body2}>{contact.notes}</span>
                </div>
              </>
            )}

            {contact.tags && contact.tags.length > 0 && (
              <>
                <ToolDivider />
                <div style={{ paddingLeft: spacing[5], paddingRight: spacing[5], paddingTop: spacing[4], paddingBottom: spacing[4] }}>
                  <ToolSectionHeader icon={<LocalOffer />} label="Tags" muted />
                  <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap" }}>
                    {contact.tags.map((tag, i) => (
                      <Tag key={i} size="sm" variant="default">
                        {displayValue(tag)}
                      </Tag>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {!isLoading && !contact && !isError && (
          <ToolEmptyState icon={<PersonOff />} message="Contact not found" />
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default ContactDetailDisplay;
